/**
 * CaseTrackUS - Google OAuth Registration Completion
 * Shown to new users who sign in with Google for the first time.
 * They must select account type and agree to Terms before account is created.
 */

import React, { useState } from 'react';
import { Container, Box, Typography, Button, CircularProgress, Alert, TextField } from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://casetrackus.com';

export default function GoogleRegister({ onLogin }) {
  const params = new URLSearchParams(window.location.search);
  const googleEmail = params.get('google_email') || '';
  const googleName = params.get('google_name') || '';
  const googleId = params.get('google_id') || '';
  const googleAvatar = params.get('google_avatar') || '';

  const [accountType, setAccountType] = useState('');
  const [displayName, setDisplayName] = useState(googleName);
  const [agreed, setAgreed] = useState(false);
  const [agreedPro, setAgreedPro] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = accountType && agreed && displayName.trim() && (accountType === 'individual' || agreedPro);

  const handleComplete = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/auth/google-complete-registration`, null, {
        params: {
          google_id: googleId,
          google_email: googleEmail,
          google_name: displayName,
          google_avatar: googleAvatar,
          account_type: accountType
        }
      });
      const { access_token, user } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      onLogin(user);
      window.history.replaceState({}, document.title, '/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Container maxWidth="sm">
        <Box sx={{ background: 'white', borderRadius: 3, p: 4, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>

          {/* Header */}
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#2E7D32', textAlign: 'center', mb: 0.5 }}>
            🗽 CaseTrackUS
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#666', mb: 3, fontSize: '0.9rem' }}>
            Welcome, {googleName}! One more step to complete your registration.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Account type selection */}
          {!accountType && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: '#666', textAlign: 'center', fontWeight: 600 }}>
                Select your account type:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box onClick={() => setAccountType('individual')}
                  sx={{ border: '2px solid #E0E0E0', borderRadius: 2, p: 2.5, cursor: 'pointer', textAlign: 'center',
                    '&:hover': { borderColor: '#2E7D32', background: '#F1F8E9' } }}>
                  <Typography sx={{ fontSize: '2rem', mb: 1 }}>👤</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>Individual</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.4 }}>
                    For immigrants tracking their own cases and family members
                  </Typography>
                </Box>
                <Box onClick={() => setAccountType('professional')}
                  sx={{ border: '2px solid #E0E0E0', borderRadius: 2, p: 2.5, cursor: 'pointer', textAlign: 'center',
                    '&:hover': { borderColor: '#2E7D32', background: '#F1F8E9' } }}>
                  <Typography sx={{ fontSize: '2rem', mb: 1 }}>🏢</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>Professional</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.4 }}>
                    For attorneys, law firms and accredited representatives
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* After selection */}
          {accountType && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }}
                onClick={() => { setAccountType(''); setAgreed(false); setAgreedPro(false); }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#2E7D32' }}>← Change account type</Typography>
              </Box>

              <Box sx={{ background: '#F1F8E9', borderRadius: 1, p: 1.5, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '1.2rem' }}>{accountType === 'individual' ? '👤' : '🏢'}</Typography>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#2E7D32' }}>
                    {accountType === 'individual' ? 'Individual Account' : 'Professional Account'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#555' }}>
                    Signing in as: {googleEmail}
                  </Typography>
                </Box>
              </Box>

              {/* Name field */}
              <TextField
                fullWidth
                label={accountType === 'professional' ? 'Law Firm / Company Name (or your full name if solo attorney)' : 'Full Name'}
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                helperText="Please verify or update your name before continuing"
                sx={{ mb: 3 }}
              />

              {/* Terms checkboxes */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                  <input type="checkbox" id="agree" checked={agreed}
                    onChange={e => setAgreed(e.target.checked)}
                    style={{ marginTop: 3, marginRight: 8, cursor: 'pointer', width: 16, height: 16 }} />
                  <label htmlFor="agree" style={{ fontSize: 13, color: '#555', cursor: 'pointer', lineHeight: 1.4 }}>
                    I have read and agree to the{' '}
                    <a href="/terms" target="_blank" style={{ color: '#2E7D32' }}>Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy" target="_blank" style={{ color: '#2E7D32' }}>Privacy Policy</a>
                  </label>
                </Box>
                {accountType === 'professional' && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <input type="checkbox" id="agreePro" checked={agreedPro}
                      onChange={e => setAgreedPro(e.target.checked)}
                      style={{ marginTop: 3, marginRight: 8, cursor: 'pointer', width: 16, height: 16 }} />
                    <label htmlFor="agreePro" style={{ fontSize: 13, color: '#555', cursor: 'pointer', lineHeight: 1.4 }}>
                      I confirm I am a licensed immigration attorney, accredited representative, or authorized law firm employee using this Service in a professional capacity
                    </label>
                  </Box>
                )}
              </Box>

              <Button fullWidth variant="contained" size="large"
                onClick={handleComplete} disabled={!canSubmit || loading}
                sx={{ background: '#2E7D32', '&:hover': { background: '#1B5E20' }, py: 1.5, borderRadius: 2 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Complete Registration'}
              </Button>
            </Box>
          )}

        </Box>
      </Container>
    </Box>
  );
}
